import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Building2,
  ClipboardList,
  Euro,
  ExternalLink,
  FileBadge,
  Layers,
  LayoutDashboard,
  Menu,
  MessageSquare,
  Barrel,
  ReceiptEuro,
  Truck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DARK_HEADER_CLOSE } from "@/lib/dialogStyles";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { brand } from '@/lib/branding';

type NavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  // Prefixe(s) consideres comme "actifs" (ex: /chantiers couvre /chantiers/:id)
  matchPrefixes?: string[];
};

type ExtraLink = {
  id: string;
  label: string;
  icon: LucideIcon;
  path?: string;
  url?: string;
  external?: boolean;
};

const CLIENT_MAIN: NavItem[] = [
  { id: "dashboard", label: "Accueil", icon: LayoutDashboard, path: "/" },
  {
    id: "chantiers",
    label: "Chantiers",
    icon: Building2,
    path: "/chantiers",
    matchPrefixes: ["/chantiers"],
  },
  {
    id: "transports",
    label: "Transports",
    icon: Truck,
    path: "/transports",
    matchPrefixes: ["/transports"],
  },
  { id: "demandes", label: "Demandes", icon: MessageSquare, path: "/demandes" },
];

const CLIENT_EXTRA: ExtraLink[] = [
  { id: "cap", label: "Mes Déchets", icon: Barrel, path: "/cap" },
  { id: "facturation", label: "Devis & Factures", icon: ReceiptEuro, path: "/devis-factures" },
  { id: "documents", label: "Documents", icon: FileBadge, path: "/documents" },
  { id: "mes-entreprises", label: "Mes entreprises travaux", icon: Building2, path: "/mes-entreprises" },
  { id: "mon-etablissement", label: "Mon établissement", icon: Building2, path: "/mon-etablissement" },
];

const ADMIN_MAIN: NavItem[] = [
  { id: "dashboard", label: "Accueil", icon: LayoutDashboard, path: "/" },
  {
    id: "operations",
    label: "Opérations",
    icon: ClipboardList,
    path: "/operations",
    // /operations et /operations/:tab — mais pas /operations/demandes (couvert par Demandes)
    matchPrefixes: ["/operations"],
  },
  { id: "facturation", label: "Devis & Factures", icon: ReceiptEuro, path: "/devis-factures" },
  {
    id: "demandes",
    label: "Demandes",
    icon: MessageSquare,
    path: "/operations/demandes",
  },
];

const ADMIN_EXTRA: ExtraLink[] = [
  { id: "etablissements", label: "Établissements", icon: Users, path: "/etablissements" },
  { id: "prix", label: "Gestion des prix", icon: Euro, path: "/prix" },
  { id: "types-categories", label: "Types & Catégories", icon: Layers, path: "/types-categories" },
  { id: "documents-admin", label: `Documents ${brand.name} & Partenaires`, icon: FileBadge, path: "/documents-admin" },
  { id: "supervision", label: "Supervision", icon: Activity, path: "/supervision" },
  {
    id: "carbone",
    label: "Modèles PDF (Carbone)",
    icon: ExternalLink,
    url: "https://carbone.io/",
    external: true,
  },
  {
    id: "brevo",
    label: "Modèles emails (Brevo)",
    icon: ExternalLink,
    url: "https://app.brevo.com/",
    external: true,
  },
];

interface BottomNavProps {
  role: "admin" | "user";
}

function isActive(item: NavItem, pathname: string): boolean {
  if (item.path === "/") return pathname === "/";
  // Cas particulier /operations/demandes (Demandes admin) doit l'emporter sur /operations
  if (item.path === "/operations/demandes") {
    return pathname.startsWith("/operations/demandes");
  }
  if (item.path === "/operations") {
    return pathname === "/operations" || (pathname.startsWith("/operations/") && !pathname.startsWith("/operations/demandes"));
  }
  if (item.matchPrefixes) {
    return item.matchPrefixes.some((p) => pathname === p || pathname.startsWith(p + "/"));
  }
  return pathname === item.path;
}

export function BottomNav({ role }: BottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [plusOpen, setPlusOpen] = useState(false);

  const mainItems = role === "admin" ? ADMIN_MAIN : CLIENT_MAIN;
  const extraItems = role === "admin" ? ADMIN_EXTRA : CLIENT_EXTRA;

  const handleNav = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExtra = (link: ExtraLink) => {
    if (link.external && link.url) {
      window.open(link.url, "_blank", "noopener,noreferrer");
    } else if (link.path) {
      handleNav(link.path);
    }
    setPlusOpen(false);
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border flex justify-around items-stretch h-[calc(4rem+env(safe-area-inset-bottom))] pb-[env(safe-area-inset-bottom)]"
      aria-label="Navigation principale"
    >
      {mainItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item, location.pathname);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleNav(item.path)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 gap-1 text-xs transition-colors",
              active
                ? "text-brand"
                : "text-muted-foreground hover:text-foreground"
            )}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="h-5 w-5" />
            <span className="truncate max-w-full px-1">{item.label}</span>
          </button>
        );
      })}

      <Sheet open={plusOpen} onOpenChange={setPlusOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="flex flex-col items-center justify-center flex-1 gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Plus de pages"
          >
            <Menu className="h-5 w-5" />
            <span>Plus</span>
          </button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-full sm:max-w-sm overflow-y-auto p-0"
          closeClassName={DARK_HEADER_CLOSE}
        >
          <SheetHeader className="px-6 py-5 bg-surface-header text-surface-header-foreground border-b border-border">
            <SheetTitle className="text-surface-header-foreground text-xl">Plus de pages</SheetTitle>
          </SheetHeader>
          <div className="py-2">
            {extraItems.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => handleExtra(link)}
                  className="w-full flex items-center gap-4 px-6 py-4 text-base text-foreground hover:bg-brand-soft hover:text-primary transition-colors"
                >
                  <Icon className="h-6 w-6 shrink-0" />
                  <span className="flex-1 text-left">{link.label}</span>
                  {link.external && <ExternalLink className="h-5 w-5 text-muted-foreground" />}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
}
